import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useFantasySeasons } from '@/features/fantasy/hooks';
import { useCreateFriendLeague } from '@/features/games/friendLeagues';
import { useProfile } from '@/features/profile/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { errorKey } from '@/lib/utils/errors';

/**
 * Prominent 1-tap entry to a 1v1 duel. Creates a duel friend-league named after
 * the user, then routes straight to its detail screen where the deep-link share
 * is ready - turning "challenge a friend" from a buried flow into a single tap.
 */
export function ChallengeFriendCard({ className }: { className?: string }) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const router = useRouter();
  const toast = useToast();
  const { userId } = useAuth();
  const profile = useProfile();
  const seasons = useFantasySeasons();
  const create = useCreateFriendLeague();

  const wcSeason =
    (seasons.data ?? []).find((s) => (s.name ?? '').toLowerCase().includes('world cup')) ??
    seasons.data?.[0];

  const onPress = () => {
    if (create.isPending) return;
    const me = (profile.data?.display_name ?? '').trim();
    const name = (me ? t('challengeFriend.duelName', { name: me }) : t('challengeFriend.duelFallback'))
      .slice(0, 40);
    create.mutate(
      { name, isDuel: true, seasonId: wcSeason?.id },
      {
        onSuccess: (res) => router.push(`/games/friend-league/${res.id}` as never),
        onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
      },
    );
  };

  const disabled = !userId || create.isPending;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      className={`mx-4 active:opacity-90 ${disabled ? 'opacity-60' : ''} ${className ?? ''}`}
    >
      <Card className="overflow-hidden p-0" elevated>
        <LinearGradient
          colors={['rgba(57,255,20,0.20)', 'rgba(214,0,255,0.10)', 'rgba(255,255,255,0)']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        />
        <View className="flex-row items-center gap-3.5 px-4 py-4">
          <View className="h-12 w-12 items-center justify-center rounded-2xl border border-volt-line bg-volt-glass">
            <Ionicons
              name={create.isPending ? 'hourglass-outline' : 'flash'}
              size={24}
              color={palette.voltBright}
            />
          </View>
          <View className="flex-1">
            <View className="flex-row items-center gap-2">
              <AppText variant="heading" weight="bold" className="text-ink" numberOfLines={1}>
                {t('challengeFriend.title')}
              </AppText>
              <View className="rounded-full bg-volt-glass px-2 py-0.5">
                <AppText variant="micro" weight="bold" className="text-volt-bright">
                  {t('challengeFriend.tag')}
                </AppText>
              </View>
            </View>
            <AppText variant="micro" className="mt-0.5 leading-4 text-soft" numberOfLines={2}>
              {t('challengeFriend.subtitle')}
            </AppText>
          </View>
          <View className="h-9 w-9 items-center justify-center rounded-full bg-volt-glass">
            <Ionicons name="people" size={18} color={palette.voltBright} />
          </View>
        </View>
      </Card>
    </Pressable>
  );
}
